import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Mail, Lock, CheckCircle, Loader2 } from "lucide-react";
import { emailUpdateService } from "@/services/emailUpdateService";
import { isValidEmail } from "@/utils/securityUtils";
import { supabase } from "@/integrations/supabase/client";

interface EmailUpdateDialogProps {
  currentEmail: string;
  userType: 'seller' | 'buyer';
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

type LoadingState = 'idle' | 'verifying-password' | 'requesting-change' | 'validating';

const REQUEST_TIMEOUT = 10000; // 10 seconds

const withTimeout = <T,>(promise: Promise<T>, timeoutMs: number): Promise<T> => {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) => 
      setTimeout(() => reject(new Error('Request timed out. Please check your connection and try again.')), timeoutMs)
    )
  ]);
};

export const EmailUpdateDialog = ({
  currentEmail,
  userType,
  open,
  onOpenChange,
  onSuccess
}: EmailUpdateDialogProps) => {
  const [newEmail, setNewEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [loadingState, setLoadingState] = useState<LoadingState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [timeoutError, setTimeoutError] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setValidationError(null);
    setTimeoutError(false);

    // Client-side validation
    if (!newEmail || !currentPassword) {
      setValidationError("Please fill in all fields");
      return;
    }

    if (newEmail.toLowerCase() === currentEmail.toLowerCase()) {
      setValidationError("New email must be different from current email");
      return;
    }

    if (!isValidEmail(newEmail)) {
      setValidationError("Please enter a valid email address");
      return;
    }

    try {
      // Step 1: Verify password and request change
      setLoadingState('verifying-password');
      
      const result = await withTimeout(
        emailUpdateService.requestEmailChange(newEmail, currentPassword),
        REQUEST_TIMEOUT
      );

      if (result.success) {
        // Step 2: Validate email change was registered
        setLoadingState('validating');
        
        // Give Supabase a moment to update the user object
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Verify new_email was set
        const { data: { user } } = await supabase.auth.getUser();
        
        if (user?.new_email && user.new_email.toLowerCase() === newEmail.toLowerCase()) {
          setSuccess(true);
          setLoadingState('idle');
          onSuccess();
          // Don't auto-close - let user read the message and close manually
        } else {
          throw new Error('Email change request was not registered. Please try again.');
        }
      } else {
        setError(result.error || "Failed to request email change");
        setLoadingState('idle');
      }
    } catch (err: any) {
      if (err.message?.includes('timed out')) {
        setTimeoutError(true);
        setError('Request timed out. Please check your internet connection and try again.');
      } else {
        setError(err.message || "An unexpected error occurred. Please try again.");
      }
      setLoadingState('idle');
    }
  };

  const handleTryAgain = () => {
    setError(null);
    setTimeoutError(false);
    setValidationError(null);
  };

  const handleClose = () => {
    setNewEmail("");
    setCurrentPassword("");
    setError(null);
    setValidationError(null);
    setTimeoutError(false);
    setSuccess(false);
    setLoadingState('idle');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Change Email Address</DialogTitle>
          <DialogDescription>
            Enter your new email address and current password to continue. We'll send a verification email to your new address.
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <Alert className="border-green-500 bg-green-50 dark:bg-green-950/20">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800 dark:text-green-200">
              <strong>✓ Verification Email Sent!</strong>
              <p className="mt-2">
                Please check <strong>{newEmail}</strong> and click the verification link to complete your email change.
              </p>
              <p className="mt-2 text-sm text-green-700 dark:text-green-300">
                You can close this dialog now. A banner will appear above until you verify.
              </p>
            </AlertDescription>
          </Alert>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Loading indicator with steps */}
            {loadingState !== 'idle' && (
              <Alert className="border-blue-500 bg-blue-50 dark:bg-blue-950/20">
                <Loader2 className="h-4 w-4 text-blue-600 animate-spin" />
                <AlertDescription className="text-blue-800 dark:text-blue-200">
                  {loadingState === 'verifying-password' && 'Verifying your password...'}
                  {loadingState === 'requesting-change' && 'Requesting email change...'}
                  {loadingState === 'validating' && 'Confirming request...'}
                </AlertDescription>
              </Alert>
            )}
            {/* Current Email (Read-only) */}
            <div className="space-y-2">
              <Label htmlFor="current-email">Current Email</Label>
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <Input
                  id="current-email"
                  type="email"
                  value={currentEmail}
                  disabled
                  className="bg-muted cursor-not-allowed"
                />
              </div>
            </div>

            {/* New Email */}
            <div className="space-y-2">
              <Label htmlFor="new-email">New Email Address</Label>
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <Input
                  id="new-email"
                  type="email"
                  placeholder="your.new@email.com"
                  value={newEmail}
                  onChange={(e) => {
                    setNewEmail(e.target.value);
                    setValidationError(null);
                    setError(null);
                  }}
                  disabled={loadingState !== 'idle'}
                  required
                />
              </div>
            </div>

            {/* Current Password */}
            <div className="space-y-2">
              <Label htmlFor="current-password">Current Password</Label>
              <div className="flex items-center gap-2">
                <Lock className="h-4 w-4 text-muted-foreground" />
                <Input
                  id="current-password"
                  type="password"
                  placeholder="Enter your current password"
                  value={currentPassword}
                  onChange={(e) => {
                    setCurrentPassword(e.target.value);
                    setValidationError(null);
                    setError(null);
                  }}
                  disabled={loadingState !== 'idle'}
                  required
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Required for security verification
              </p>
            </div>

            {/* Validation Error */}
            {validationError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{validationError}</AlertDescription>
              </Alert>
            )}

            {/* Server Error with Try Again */}
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <div className="flex items-start justify-between gap-2">
                    <span className="flex-1">{error}</span>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleTryAgain}
                      className="shrink-0"
                    >
                      Try Again
                    </Button>
                  </div>
                </AlertDescription>
              </Alert>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={loadingState !== 'idle'}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={loadingState !== 'idle'}>
                {loadingState !== 'idle' ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  'Send Verification Email'
                )}
              </Button>
            </DialogFooter>
          </form>
        )}

        {/* Success footer with manual close */}
        {success && (
          <DialogFooter>
            <Button onClick={handleClose}>Close</Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
